import type { BarcodeFormat, Item } from '@erp/shared';
import { Barcode, History, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Card, Skeleton } from '../../../components/shadcn/index.js';
import { Button, CopyButton, EmptyState } from '../../../components/ui/index.js';
import { api } from '../../../lib/api.js';
import { formatDateTime } from '../../../lib/intl.js';
import { BarcodePreview } from '../BarcodePreview.js';
import { printItemBarcode } from '../printBarcode.js';

import { guessBarcodeFormat } from './profileFormat.js';
import { SectionTitle } from './ProfileParts.js';

interface CodeRow {
  code: string;
  format: BarcodeFormat;
  /** null = the base unit. */
  unitName: string | null;
  factor: string | null;
  primary: boolean;
  /** A hidden unit's codes do not scan. */
  scans: boolean;
}

/**
 * Every code that finds this product at a scanner: the item's own barcode and
 * its extra aliases (they sell ONE piece), then each pack's barcode and aliases
 * (they sell the PACK). A hidden pack's codes are listed but marked — they do
 * not scan until the pack is shown again.
 */
export function ItemBarcodesTab({ item }: { item: Item }): JSX.Element {
  const { t } = useTranslation('items');
  const rows: CodeRow[] = [];
  if (item.barcode) rows.push({ code: item.barcode, format: item.barcodeFormat, unitName: null, factor: null, primary: true, scans: true });
  for (const c of item.barcodes ?? []) rows.push({ code: c, format: guessBarcodeFormat(c), unitName: null, factor: null, primary: false, scans: true });
  for (const u of item.units) {
    if (u.barcode) rows.push({ code: u.barcode, format: guessBarcodeFormat(u.barcode), unitName: u.name, factor: u.factor, primary: true, scans: u.isActive });
    for (const c of u.barcodes ?? []) rows.push({ code: c, format: guessBarcodeFormat(c), unitName: u.name, factor: u.factor, primary: false, scans: u.isActive });
  }

  if (rows.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState icon={<Barcode className="h-7 w-7" aria-hidden />} title={t('profile.codes.empty', { defaultValue: 'This product has no barcode yet' })} />
      </Card>
    );
  }

  const print = (r: CodeRow): void => printItemBarcode({
    sku: item.code,
    name: r.unitName ? `${item.name} — ${r.unitName}` : item.name,
    barcode: r.code,
    format: r.format,
    errorLabel: t('actions.printNoBarcode', { defaultValue: 'No barcode to print' }),
  });

  return (
    <Card className="p-4">
      <SectionTitle icon={Barcode}>{t('profile.codes.title', { defaultValue: 'Barcodes' })}</SectionTitle>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((r) => (
          <div key={`${r.unitName ?? ''}-${r.code}`} className={`flex flex-col gap-2 rounded-xl border border-border p-3 ${r.scans ? '' : 'opacity-60'}`}>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant={r.unitName ? 'info' : 'secondary'}>
                {r.unitName
                  ? t('profile.codes.pack', { defaultValue: '{{unit}} · ×{{factor}}', unit: r.unitName, factor: Number(r.factor) })
                  : t('profile.codes.single', { defaultValue: 'Single · {{unit}}', unit: item.unitOfMeasure })}
              </Badge>
              <Badge variant={r.primary ? 'success' : 'outline'}>
                {r.primary ? t('profile.codes.primary', { defaultValue: 'Main code' }) : t('profile.codes.alias', { defaultValue: 'Extra code' })}
              </Badge>
              {!r.scans && <Badge variant="warning">{t('profile.codes.noScan', { defaultValue: 'Pack hidden — does not scan' })}</Badge>}
            </div>
            {/* Barcodes read left to right whatever the page direction. */}
            <div dir="ltr" className="flex min-h-[64px] items-center justify-center rounded-lg border border-border bg-bg p-2">
              <BarcodePreview value={r.code} format={r.format} className="max-h-16 max-w-full" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span dir="ltr" className="truncate font-mono text-sm">{r.code}</span>
              <span className="flex items-center gap-1">
                <CopyButton value={r.code} subject={t('detail.barcode', { defaultValue: 'Barcode' })} />
                <Button type="button" variant="ghost" size="sm" onClick={() => print(r)} title={t('actions.print', { defaultValue: 'Print' })} aria-label={t('actions.print', { defaultValue: 'Print' })}>
                  <Printer className="h-4 w-4" aria-hidden />
                </Button>
              </span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

interface AuditRow {
  id: string;
  actorName: string | null;
  action: string;
  operation: string | null;
  createdAt: string;
  diff: unknown;
  beforeState: unknown;
  afterState: unknown;
}

const OPERATION_TONE: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'danger',
};

/** Bookkeeping columns every write touches — noise in a list of what changed. */
const QUIET = new Set(['updated_at', 'updatedAt', 'created_at', 'createdAt', 'tenant_id', 'id']);

/** A short, printable form of a stored value — or null when it is not worth showing. */
function brief(v: unknown): string | null {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'string') return v.length > 40 ? null : v;
  return null;
}

/**
 * What one change did, field by field. The audit engine stores the row before
 * and after an UPDATE; a hand-written audit entry carries a `diff` instead.
 */
function changes(r: AuditRow): Array<{ field: string; from: string | null; to: string | null }> {
  const asObj = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null);
  const before = asObj(r.beforeState);
  const after = asObj(r.afterState);
  if (r.operation === 'UPDATE' && before && after) {
    return Object.keys(after)
      .filter((k) => !QUIET.has(k) && JSON.stringify(before[k]) !== JSON.stringify(after[k]))
      .slice(0, 8)
      .map((k) => ({ field: k, from: brief(before[k]), to: brief(after[k]) }));
  }
  const diff = asObj(r.diff);
  return diff ? Object.keys(diff).filter((k) => !QUIET.has(k)).slice(0, 8).map((k) => ({ field: k, from: null, to: null })) : [];
}

/**
 * Who changed this product and when — its own audit trail (`/audit-logs?entityId=`).
 * Rendered only for holders of `audit_logs.read`; a refusal reads as empty.
 */
export function ItemActivityTab({ itemId }: { itemId: string }): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const [rows, setRows] = useState<AuditRow[] | null>(null);

  useEffect(() => {
    let active = true;
    setRows(null);
    void api()
      .get<{ items: AuditRow[] }>('/audit-logs', { params: { entityId: itemId, limit: 200 } })
      .then((r) => { if (active) setRows(r.data.items); })
      .catch(() => { if (active) setRows([]); });
    return () => { active = false; };
  }, [itemId]);

  if (rows === null) return <Card className="p-4"><Skeleton className="h-40 w-full" /></Card>;
  if (rows.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState
          icon={<History className="h-8 w-8" aria-hidden />}
          title={t('profile.activity.empty', { defaultValue: 'No recorded changes yet' })}
          description={t('profile.activity.emptyHint', { defaultValue: 'Edits to this product will appear here.' })}
        />
      </Card>
    );
  }

  return (
    <Card className="p-0">
      <ol className="divide-y divide-border">
        {rows.map((r) => {
          const fields = changes(r);
          return (
            <li key={r.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
              {r.operation && (
                <Badge variant={OPERATION_TONE[r.operation] ?? 'secondary'}>
                  {t(`profile.activity.op.${r.operation}`, { defaultValue: r.operation })}
                </Badge>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-fg">{r.action}</div>
                {fields.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {fields.map((f) => (
                      <span key={f.field} className="rounded-md bg-bg-subtle px-1.5 py-0.5 font-mono text-[11px] text-fg-muted">
                        {f.field}
                        {f.from !== null && f.to !== null && <span dir="ltr" className="ms-1 text-fg">{f.from} → {f.to}</span>}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-xs text-fg-muted">{r.actorName ?? t('profile.system', { defaultValue: 'System' })}</span>
              <span className="whitespace-nowrap text-xs text-fg-subtle">{formatDateTime(r.createdAt, { locale: i18n.language })}</span>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
